// Deciding whether a pulled tombstone is a real deletion, and repairing the device if it is.
//
// Extracted from DatabaseWrapper so it can be unit-tested without pulling in
// pouchdb-browser, which references `self` and fails to load under node. It was
// split out of database-wrapper-passthrough.ts, which had become the shared home
// for every testable sync helper regardless of subject (deferred finding
// df-08d4a072f17b).

import { isPouchNotFound } from '@cf-shared/data-model/pouch';

// ---------------------------------------------------------------------------
// #4441 — deciding whether a pulled conflict-close tombstone is a real deletion
//
// When the conflict auto-resolve (#4314) closes a losing branch, it writes a
// deletion marker — a tombstone — on that branch, carrying the field
// `db__conflictClose: true`. The pull handler in database-wrapper.ts reads that
// field to avoid treating a branch close as if the whole document had been
// deleted.
//
// The field alone is not enough to conclude that. It says "THIS REVISION closed
// a branch". It does not say "this DOCUMENT still has a living version". Those
// two answers come apart once every branch of the document is deleted: the user
// genuinely deletes the document on one branch while the resolver closes the
// other. CouchDB then has to pick a winner among deleted revisions, and about
// half the time it picks the flagged one — so a flagged tombstone arrives for a
// document that is really gone.
//
// This helper asks the local database the question the pull handler actually
// needs answered, and drives BOTH consequences off that one answer: evicting the
// identity-map entry, and telling the reactive listeners a day changed.
//
// #4464 WIDENED THIS TO EVERY PULLED DELETION, and gave the "still alive" answer
// something to do. Here is why.
//
// The pull direction asks the server for winning revisions only
// (`style: 'main_only'`). A deleted revision can never win while a live one
// exists, so a tombstone that arrives on the pull is ALWAYS the server's winner,
// and that means every branch on the server is deleted — the document is
// genuinely gone there. So if the document still resolves ALIVE locally after
// that tombstone was applied, this device is holding a branch the server has
// already closed, and it will never be offered the tombstone that closes it: that
// revision is not the winner, `main_only` never mentions it, and the changes feed
// has already moved past the document. Without a repair the device shows a
// document the user deleted, for ever.
//
// That is true of ORDINARY deletions too, not only conflict-close ones. Which of
// the two tombstones wins is a revision-hash tie-break, so each is reached about
// half the time. The pull loop still handles an ordinary deletion immediately and
// pays nothing extra for it; entries carrying `alreadyHandledInLoop` are here
// only so the aliveness question gets asked about them as well.
//
// See design-docs/sync-integrity.md ("Two traps that make this harder than it
// looks").
// ---------------------------------------------------------------------------

/** What one #4464 repair actually managed to copy down from the server. */
export interface AdoptedRemoteRevisions {
  /** Deleted branch tips written into the local revision tree. */
  deletedLeavesWritten: number;
  /** Branch tips the server answered as `{ missing: <rev> }` and could not return. */
  missingLeaves: number;
  /**
   * The server still has a LIVE branch tip. The document is not deleted there
   * after all — somebody re-created it — so there was never anything to repair.
   */
  sawLiveLeaf: boolean;
}

/**
 * How a #4464 repair of one out-of-step document turned out.
 *
 * The three benign outcomes are separated on purpose. "The document is still
 * alive after the repair" has several causes, and only some of them are
 * harmless — folding them together would report a device that is still showing
 * a deleted document as if nothing were wrong.
 */
export enum PulledDeletionRepairOutcome {
  /** The graft closed the local branch: the document is gone now, as intended. */
  Repaired = 'Repaired',
  /**
   * The server's tombstones were copied down and the document is STILL alive
   * here — so this device is holding a live revision newer than anything the
   * server has, an edit made offline and not pushed yet. Benign and
   * self-healing: that revision keeps winning locally, and the next push offers
   * it, so the document comes back on the server as an honest conflict.
   */
  StillAliveNewerLocalRevision = 'StillAliveNewerLocalRevision',
  /**
   * The server has a live branch tip, so the document is not deleted there any
   * more — somebody re-created it. Benign: there was nothing to repair, and the
   * ordinary pull delivers the live revision.
   */
  ServerRevisionIsAliveAgain = 'ServerRevisionIsAliveAgain',
  /**
   * The device is STILL out of step and nothing will offer it that tombstone
   * again — the replication checkpoint has already moved past it. Either the
   * remote read or the local graft failed, or nothing could be copied down at
   * all while the document is still alive here.
   */
  Failed = 'Failed',
}

/** One pulled tombstone the settle step has to make a decision about. */
export interface PulledDeletionToSettle {
  docId: string;
  /**
   * The cached body, read BEFORE anything evicted it. A pulled tombstone is
   * only `{_id, _rev, _deleted}` and carries no type and no date, so this body
   * is the only way a listener can tell which calendar day was affected. Null
   * when the document was not in the identity map.
   */
  cachedBody: any | null;
  /** The raw pulled tombstone, used when there is no cached body to send. */
  tombstone: any;
  /**
   * Did the pull loop ALREADY evict this document and publish its deletion?
   *
   * True for an ordinary deletion, which is unambiguous and is handled straight
   * away inside the loop, and false for a conflict-close one, which is deferred
   * whole.
   *
   * It decides two things here, and they are opposite sides of the same fact —
   * that the loop has already acted on this entry.
   *
   * When the very first aliveness read says the document is gone, an entry that
   * was already handled is left alone. Running the deletion handling a second
   * time would evict twice, publish the same deletion twice (so the calendar
   * recompute runs twice per batch), and — worst — fire
   * `ConflictCloseTombstoneWasRealDeletion` for every ordinary deletion, which
   * would destroy the #4441 measurement that report exists for.
   *
   * When the document turns out to be still alive, the same flag says whose
   * eviction has to be UNDONE: only the loop evicted, so only such an entry has
   * anything to put back. See `restoreCacheEntryIfAbsent`.
   *
   * It deliberately does NOT suppress the handling after a #4464 repair. There
   * the loop's own eviction and publish happened while the document was still
   * alive, so a watcher that re-read at that moment re-read a living document.
   * The repair is what makes it gone, so the second round is needed, not
   * duplicate.
   */
  alreadyHandledInLoop: boolean;
}

/** The wrapper-side calls this helper needs, injected so it is node-testable. */
export interface PulledDeletionSettleDeps {
  /**
   * Does this document still have a living version in the LOCAL database?
   *
   * Throws if the read itself failed. "I could not look" is deliberately NOT
   * folded into `false` — see the unknown-answer handling below.
   */
  isDocumentAliveAsync: (docId: string) => Promise<boolean>;
  /**
   * Bring this document's revisions back into step with the server (#4464).
   *
   * Called when a pulled tombstone left the document still alive here, which
   * means this device is holding a branch the server has already closed.
   *
   * The implementation copies the server's OWN revisions down and adds them to
   * the local revision tree. It must never delete a local revision to make the
   * document go away: the live local revision can be an edit the user made
   * offline and has not pushed yet, and deleting it would be silent data loss.
   * Adding the server's revisions cannot destroy anything — the server's
   * tombstone closes the local branch precisely because that branch is its
   * parent, and a genuinely newer local revision simply keeps winning.
   *
   * Answers what it managed to copy down, which is what separates a benign
   * outcome from a device that is still out of step. Throws when the document
   * could not be brought back into step at all.
   */
  repairServerDeletedDocAsync: (docId: string) => Promise<AdoptedRemoteRevisions>;
  /**
   * Can the local database still be read at all?
   *
   * A settle runs fire-and-forget after the sync handler has returned, so a
   * sign-out wipe, a shutdown or a database recovery can take the handle away
   * underneath it. Any of those means the remaining reads would fail, so the run
   * stops rather than producing one failure per remaining document. Whether that
   * stop is worth reporting is the CALLER's decision, made in
   * `reportStoppedEarly` — the two causes are not equally benign.
   */
  isDatabaseUnreadable: () => boolean;
  /** Drop the identity-map entry for a document that is genuinely gone. */
  evictFromCache: (docId: string) => void;
  /**
   * Put back an identity-map entry the pull loop dropped too early, but ONLY if
   * nothing has claimed that document's slot since.
   *
   * The implementation must copy no document content and must never replace a
   * live entry; breaking either turns this repair into the #4669 loss it is
   * undoing. The reasoning is in `design-docs/sync-integrity.md`, "Putting back
   * an entry the loop dropped too early".
   *
   * Answers whether it actually restored, so the caller can count it.
   */
  restoreCacheEntryIfAbsent: (docId: string, body: any) => boolean;
  /** Tell the document watchers this document changed. */
  notifyWatchers: (docId: string) => void;
  /** Hand the real deletions to the pulled-doc listeners, one batch per run. */
  publishDeletions: (docs: any[]) => void;
  /** The aliveness read failed. Never swallowed. */
  reportProbeFailure: (docId: string, err: unknown) => void;
  /**
   * The run stopped because the database became unreadable, leaving
   * `unsettledCount` tombstones undecided. Those do not come back — the
   * replication checkpoint has already moved past them — so the caller decides
   * whether this particular cause is benign.
   */
  reportStoppedEarly: (unsettledCount: number) => void;
  /**
   * A flagged tombstone turned out to be a real deletion.
   *
   * `hadCachedBody` says whether the identity map was holding this document when
   * the tombstone arrived — see `realDeletionCount` for why the caller wants it.
   */
  reportRealDeletion: (docId: string, hadCachedBody: boolean) => void;
  /**
   * How a #4464 repair turned out (`err` is set only for `Failed`).
   *
   * The three outcomes are NOT equally serious, and the caller decides how to
   * report each — see `PulledDeletionRepairOutcome`. Only `Failed` leaves
   * anything unresolved.
   */
  reportRepairOutcome: (docId: string, outcome: PulledDeletionRepairOutcome, err?: unknown) => void;
}

/** What one settle run found, for logging and tests. */
export interface PulledDeletionSettleResult {
  /**
   * Flagged tombstones whose document does not resolve locally any more.
   *
   * Read this as an UPPER BOUND on how often the all-branches-deleted race is
   * reached, not an exact count. A local not-found answers "this document has no
   * living version here", which is true both when every branch really was
   * deleted and when the device never held the document in the first place. The
   * handling is right either way — there is nothing to evict, and a listener
   * told about a document it never had recomputes a day for nothing. Only the
   * measurement is affected, which is why `reportRealDeletion` also passes
   * whether the identity map held the document.
   */
  realDeletionCount: number;
  /**
   * Tombstones the PULL LOOP had already handled, and whose document is indeed
   * gone. Nothing was done here — this is the ordinary deletion, counted only so
   * a log line can say the run was not idle.
   */
  alreadyHandledCount: number;
  /**
   * Documents still alive after their tombstone was applied — the #4464
   * out-of-step state — which are therefore sent for repair.
   *
   * The FOUR counts below say how those repairs turned out. Do not expect them
   * to add up to this one — the wrapper prints all of these side by side in one
   * log line, and two entries land outside the four:
   *
   *  - a run that stopped early with a repair still to do;
   *  - a post-graft aliveness read that threw, which goes to `unknownCount`.
   */
  stillAliveCount: number;
  /** Repairs that worked: the document is gone now. */
  repairedCount: number;
  /**
   * Repairs that worked but left the document alive, because this device holds a
   * newer unpushed revision. Benign — see `PulledDeletionRepairOutcome`.
   */
  stillAliveAfterRepairCount: number;
  /**
   * Documents the server has alive again, so nothing was out of step. Also
   * benign — see `PulledDeletionRepairOutcome`.
   */
  serverAliveAgainCount: number;
  /** Repairs that failed. The device is still out of step for this document. */
  repairFailedCount: number;
  /**
   * Tombstones whose aliveness answer never arrived, so nothing could be
   * decided about them. Two ways in: the FIRST read failed, or a repair ran and
   * the read AFTER it failed — in the second case the document's aliveness was
   * read once successfully, but the answer that mattered was the later one.
   */
  unknownCount: number;
  /**
   * Identity-map entries the pull loop dropped too early and this run put back,
   * because the document turned out to still be alive.
   *
   * Expect this to be SMALLER than the number of entries that qualified: the
   * restore stands down when something has already registered a fresh object,
   * and that stand-down is the correct outcome rather than a missed repair.
   */
  cacheEntryRestoredCount: number;
  /** True when the run stopped early because the database became unreadable. */
  stoppedEarly: boolean;
}

/** The two real database calls the #4464 graft needs, injected so it is node-testable. */
export interface AdoptRemoteLeavesDeps {
  /**
   * Every branch tip the SERVER holds for this document, ancestry included.
   *
   * Realized as `get(docId, { open_revs: 'all', revs: true })`, and both options
   * are load-bearing — see `design-docs/sync-integrity.md`. Answers one entry
   * per tip: `{ ok: <revision> }` for one it could return, `{ missing: <rev> }`
   * for one it could not.
   */
  readRemoteLeavesAsync: (docId: string) => Promise<Array<{ ok?: any, missing?: string }>>;
  /**
   * Write ONE revision into the local tree without creating a new one, and hand
   * back the row the database answered with.
   *
   * One revision per call, never a batch, so a single write never carries
   * several rows for the same `_id` — the shape that used to lose a revision on
   * the iOS SQLite adapter (#4431).
   */
  writeLocalRevisionAsync: (revision: any) => Promise<{ error?: any, name?: string, reason?: string } | undefined>;
}

/**
 * Copy the server's own revisions for one document into the local tree (#4464).
 *
 * Only DELETED branch tips are written. A live one needs no repair — the
 * ordinary pull delivers those, and writing one here could carry an attachment
 * stub with no bytes behind it — but seeing one is worth reporting, because it
 * means the document is not deleted on the server after all.
 *
 * Throws when a write is refused. `bulkDocs` reports a row that did not apply in
 * its RESULT rather than throwing (#3987 A1), and #4431 records the native
 * adapter losing a revision while reporting every row `ok` — so a dropped graft
 * would otherwise look exactly like a successful repair.
 */
export async function adoptRemoteLeavesAsync(
  deps: AdoptRemoteLeavesDeps,
  docId: string,
): Promise<AdoptedRemoteRevisions> {
  const leaves = await deps.readRemoteLeavesAsync(docId);
  const adopted: AdoptedRemoteRevisions = { deletedLeavesWritten: 0, missingLeaves: 0, sawLiveLeaf: false };

  for (const leaf of leaves ?? []) {
    const revision = leaf?.ok;
    if (!revision) {
      adopted.missingLeaves++; // a `missing` entry has nothing to write
      continue;
    }
    if (!revision._deleted) {
      adopted.sawLiveLeaf = true;
      continue;
    }
    const row = await deps.writeLocalRevisionAsync(revision);
    if (row?.error) {
      throw new Error(`grafting ${revision._rev} was refused: ${row.name ?? row.error} ${row.reason ?? ''}`.trim());
    }
    adopted.deletedLeavesWritten++;
  }
  return adopted;
}

/**
 * Did a graft that already wrote something leave the document gone?
 *
 * Only used on the path where the graft THREW, so an aliveness read that also
 * fails tells us nothing new — it answers "not gone", which routes back to
 * reporting the graft failure that is the real problem.
 */
async function isDocumentGoneAfterGraftAsync(
  deps: PulledDeletionSettleDeps,
  docId: string,
): Promise<boolean> {
  try {
    return !await deps.isDocumentAliveAsync(docId);
  } catch {
    // "not gone" routes the caller back to reporting the graft failure — see above
    return false;
  }
}

/**
 * What one #4464 repair attempt concluded.
 *
 * Two separate answers, because the caller needs both and neither implies the
 * other — see `documentKnownAlive`, which is NOT derivable from `outcome`.
 */
interface PulledDeletionRepairAttempt {
  outcome: PulledDeletionRepairOutcome;
  /**
   * Did a read POSITIVELY establish that the document is still here?
   *
   * This is the answer to "is it alive", and `outcome` is the answer to "did the
   * repair work". They are different questions, and `Failed` covers both a
   * document known to be alive and a document nobody could look at:
   *
   *  - the graft copied nothing down, so the document is exactly as the first
   *    aliveness read found it — alive. TRUE.
   *  - the post-graft re-read threw, after a tombstone had already been written.
   *    The document may well be gone now. FALSE.
   *  - the graft threw, and `isDocumentGoneAfterGraftAsync` answered "not gone".
   *    That helper gives the same answer whether the document really is here or
   *    its OWN read threw, so this branch cannot tell those apart and answers
   *    FALSE for both. That skips a restore which would sometimes have been
   *    right, and erring towards not restoring is the direction to err in.
   *
   * So false means "no read established that the document is here" — a read that
   * failed, or a branch that cannot tell. It is NOT a statement that the repair
   * failed: the `Failed` exit where the graft copied nothing down answers TRUE.
   * Anything that acts on "the document is still here" must read THIS and not
   * test `outcome`, or it acts on a document that may be deleted.
   */
  documentKnownAlive: boolean;
}

/**
 * Bring one out-of-step document back in line with the server, and say how it
 * went (#4464).
 *
 * Split out of the settle loop so that loop still reads as one decision per
 * tombstone. Everything here is about a single document that was still alive
 * after its tombstone arrived.
 *
 * It counts its own outcome into `result` and reports it, so the caller has
 * exactly one thing to check for the evict-and-publish decision: `Repaired`
 * means the document is gone now. Every other answer means it should not evict.
 * The separate `documentKnownAlive` answers a different question — see the type.
 *
 * Never throws — a failed repair is an outcome, not an exception.
 */
async function repairOutOfStepDocAsync(
  deps: PulledDeletionSettleDeps,
  docId: string,
  result: PulledDeletionSettleResult,
): Promise<PulledDeletionRepairAttempt> {
  // the repair does not come back if it fails: the replication checkpoint has
  // already moved past this tombstone, so nothing will offer it again.
  // `documentKnownAlive` is passed rather than assumed, because a failure can
  // mean either "still here" or "nobody could look".
  const failed = (err: unknown, documentKnownAlive: boolean): PulledDeletionRepairAttempt => {
    result.repairFailedCount++;
    deps.reportRepairOutcome(docId, PulledDeletionRepairOutcome.Failed, err);
    return { outcome: PulledDeletionRepairOutcome.Failed, documentKnownAlive };
  };

  let adopted: AdoptedRemoteRevisions;
  try {
    adopted = await deps.repairServerDeletedDocAsync(docId);
  } catch (err) {
    // A graft that threw may still have written something: it writes one
    // revision per call and stops at the first refusal, so an earlier tip can
    // already have closed the local branch. That is why the rule below is about
    // WRITING, not about succeeding.
    //
    // `isDocumentGoneAfterGraftAsync` answers "not gone" both when the document
    // really is still here and when its own read threw, so this branch cannot
    // tell those apart — hence `documentKnownAlive: false`.
    if (!await isDocumentGoneAfterGraftAsync(deps, docId)) return failed(err, false);
    // it worked despite the error, so the document IS gone and the caller must
    // evict and publish. The partial failure still gets reported, because the
    // server's remaining branch tips were not copied down.
    deps.reportRepairOutcome(docId, PulledDeletionRepairOutcome.Failed, err);
    result.repairedCount++;
    return { outcome: PulledDeletionRepairOutcome.Repaired, documentKnownAlive: false };
  }

  // Nothing was written, so nothing here can have changed the document — no
  // re-probe is needed or possible on either of these two exits. Both therefore
  // inherit the first aliveness read, which said the document is here.
  if (adopted.deletedLeavesWritten === 0) {
    // the server's document is alive again: somebody re-created it, so there was
    // never anything out of step, and the ordinary pull delivers that revision
    if (adopted.sawLiveLeaf) {
      result.serverAliveAgainCount++;
      deps.reportRepairOutcome(docId, PulledDeletionRepairOutcome.ServerRevisionIsAliveAgain);
      return { outcome: PulledDeletionRepairOutcome.ServerRevisionIsAliveAgain, documentKnownAlive: true };
    }
    // nothing came down and the document is still here, so the branch this
    // device holds is still open and still wrong. Unresolved, not benign — the
    // usual cause is every branch tip coming back as `missing`.
    return failed(new Error(
      `no revision could be copied down (${adopted.missingLeaves} branch tip(s) missing on the server)`), true);
  }

  // Something WAS written, so ask again rather than assuming what it did. It may
  // not have killed the document: this device can be holding a newer revision it
  // has not pushed yet, which keeps winning. Publishing a deletion for a
  // document that is still alive is the #4441 bug from the other side.
  //
  // This runs even when a live tip was seen. `adoptRemoteLeavesAsync` writes the
  // deleted tips as it walks the leaves and only learns about a live one when it
  // reaches it, so a server tree holding both can have closed the local branch
  // BEFORE `sawLiveLeaf` was set. Returning "alive again" without asking would
  // then leave the identity map holding a document the database has deleted —
  // the same stale-object shape the catch above guards against.
  let aliveAfterRepair: boolean;
  try {
    aliveAfterRepair = await deps.isDocumentAliveAsync(docId);
  } catch (err) {
    // a tombstone HAS been written by now, so this document may be gone — and
    // this read is the only thing that could have said. Nobody may act on "it is
    // still here" from here on.
    result.unknownCount++;
    deps.reportProbeFailure(docId, err);
    return { outcome: PulledDeletionRepairOutcome.Failed, documentKnownAlive: false };
  }

  if (aliveAfterRepair) {
    // which benign reading applies depends on WHY it is still alive: the server
    // has it alive again, or this device holds a newer unpushed revision
    if (adopted.sawLiveLeaf) {
      result.serverAliveAgainCount++;
      deps.reportRepairOutcome(docId, PulledDeletionRepairOutcome.ServerRevisionIsAliveAgain);
      return { outcome: PulledDeletionRepairOutcome.ServerRevisionIsAliveAgain, documentKnownAlive: true };
    }
    result.stillAliveAfterRepairCount++;
    deps.reportRepairOutcome(docId, PulledDeletionRepairOutcome.StillAliveNewerLocalRevision);
    return { outcome: PulledDeletionRepairOutcome.StillAliveNewerLocalRevision, documentKnownAlive: true };
  }

  result.repairedCount++;
  deps.reportRepairOutcome(docId, PulledDeletionRepairOutcome.Repaired);
  return { outcome: PulledDeletionRepairOutcome.Repaired, documentKnownAlive: false };
}

/**
 * Settle the tombstones a pull batch delivered.
 *
 * Runs AFTER the pull loop, not inside it. The loop is the path that froze cold
 * starts in #4243, and it must stay synchronous; the aliveness answer needs a
 * database read. Deferring costs nothing here, because the document watchers
 * were already notified inside the loop and nothing reads the identity-map entry
 * synchronously from it.
 *
 * A failed aliveness read is caught and reported. Every OTHER dependency is
 * called bare, so a caller that supplies a throwing `evictFromCache`,
 * `notifyWatchers`, `publishDeletions` or any report function gets that error
 * back — wrap the call if replication must not see it, the way
 * `DatabaseWrapper.settlePulledDeletionsFromSyncAsync` does.
 *
 * **There is deliberately no cap on how much one run does**, and the reason is
 * worth stating because a cap looks like an obvious improvement. Every pulled
 * deletion costs one local read, and each out-of-step document additionally
 * costs one remote read and a graft. A big batch is real — schedule
 * regeneration bulk-deletes log entries on one device, which the next device
 * pulls as one tombstone batch. But the work is `await`ed per entry, so it never
 * holds the main thread, and it runs after the pull loop has returned, outside
 * the path that froze cold starts in #4243. What a cap would buy in elapsed time
 * it would pay for in correctness: a skipped repair never comes back, because
 * the replication checkpoint has already moved past that tombstone. So the run
 * finishes what it started, and stops only when the database can no longer be
 * read.
 */
export async function settlePulledDeletionsAsync(
  deps: PulledDeletionSettleDeps,
  tombstones: PulledDeletionToSettle[],
): Promise<PulledDeletionSettleResult> {
  const result: PulledDeletionSettleResult = {
    realDeletionCount: 0,
    alreadyHandledCount: 0,
    stillAliveCount: 0,
    repairedCount: 0,
    stillAliveAfterRepairCount: 0,
    serverAliveAgainCount: 0,
    repairFailedCount: 0,
    unknownCount: 0,
    cacheEntryRestoredCount: 0,
    stoppedEarly: false,
  };
  const deletionsToPublish: any[] = [];
  // what a document that really is gone costs: it leaves the identity map, its
  // watchers are told, and it joins the ONE delivery made at the end of the run.
  const evictAndQueuePublish = (entry: PulledDeletionToSettle): void => {
    deps.evictFromCache(entry.docId);
    deps.notifyWatchers(entry.docId);
    deletionsToPublish.push(entry.cachedBody != null
      ? { ...entry.cachedBody, _deleted: true }
      : entry.tombstone);
  };

  for (let index = 0; index < tombstones.length; index++) {
    const entry = tombstones[index];
    // if the database can no longer be read, stop rather than turn one cause
    // into one read failure per remaining tombstone. The caller is told how many
    // were left undecided, and decides whether that is worth reporting.
    if (deps.isDatabaseUnreadable()) {
      result.stoppedEarly = true;
      deps.reportStoppedEarly(tombstones.length - index);
      break;
    }

    let stillAlive: boolean;
    try {
      stillAlive = await deps.isDocumentAliveAsync(entry.docId);
    } catch (err) {
      // An unknown answer keeps the safer half of the old behaviour: do
      // nothing. Evicting the identity-map entry for a document that is in fact
      // alive is the exact bug #4314's marker exists to prevent, and a read that
      // failed is not evidence the document is gone. The failure is reported so
      // it does not disappear.
      result.unknownCount++;
      deps.reportProbeFailure(entry.docId, err);
      continue;
    }

    if (stillAlive) {
      // #4464 — this device is OUT OF STEP. The tombstone that just arrived is
      // the server's winner, so every branch on the server is deleted; a
      // document still alive here is therefore holding a branch the server has
      // already closed, and nothing will ever offer it the closing tombstone
      // again. Bring it back into step by adopting the server's own revisions.
      result.stillAliveCount++;

      // the repair WRITES, so re-check before starting it — the run has been
      // through an await since the check at the top of this iteration. This
      // entry is undecided too, hence `- index` rather than `- index - 1`.
      if (deps.isDatabaseUnreadable()) {
        result.stoppedEarly = true;
        deps.reportStoppedEarly(tombstones.length - index);
        break;
      }

      const attempt = await repairOutOfStepDocAsync(deps, entry.docId, result);
      if (attempt.outcome !== PulledDeletionRepairOutcome.Repaired) {
        // The document is still here, so the pull loop's eviction was wrong and
        // stays wrong — put the identity-map entry back. Gated on
        // `documentKnownAlive` rather than on the outcome, because two `Failed`
        // paths mean "nobody could look" and the document may in fact be gone.
        // Only an entry the LOOP evicted has anything to put back.
        // See design-docs/sync-integrity.md, "Putting back an entry the loop
        // dropped too early".
        const restorable = attempt.documentKnownAlive
          && entry.alreadyHandledInLoop
          && entry.cachedBody != null;
        if (restorable && deps.restoreCacheEntryIfAbsent(entry.docId, entry.cachedBody)) {
          result.cacheEntryRestoredCount++;
        }
        continue;
      }

      // Both kinds of entry need this, INCLUDING one the pull loop already
      // handled: the loop did that while the document was still alive, so a
      // watcher that re-read at that moment re-read a living document. The
      // repair is what made it gone.
      evictAndQueuePublish(entry);
      continue;
    }

    // The document is gone, and nothing repaired it — it simply arrived deleted.
    if (entry.alreadyHandledInLoop) {
      // An ordinary deletion, already evicted and published inside the pull
      // loop. Doing it again would recompute the calendar twice for every
      // deletion batch, and would fire `reportRealDeletion` for every ordinary
      // deletion, which would swamp the rare-window measurement that report
      // exists for (#4441).
      result.alreadyHandledCount++;
      continue;
    }

    result.realDeletionCount++;
    deps.reportRealDeletion(entry.docId, entry.cachedBody != null);
    evictAndQueuePublish(entry);
  }

  if (deletionsToPublish.length > 0) {
    deps.publishDeletions(deletionsToPublish);
  }
  return result;
}

/**
 * Ask a database whether a document still has a living version.
 *
 * The whole helper is this one mapping, and it is load-bearing: PouchDB answers
 * a DELETED document with a not-found error, exactly as it answers one that was
 * never there. That is what makes a real deletion detectable at all. Anything
 * else is a read that FAILED, and is rethrown rather than folded into "gone" —
 * treating it as an answer would evict a document that may well be alive.
 *
 * Separate from the wrapper so this mapping can be unit tested under node; the
 * wrapper supplies the real `get`.
 */
export async function isDocumentAliveInDbAsync(
  getDocAsync: (docId: string) => Promise<unknown>,
  docId: string,
): Promise<boolean> {
  try {
    await getDocAsync(docId);
    return true;
  } catch (err) {
    if (isPouchNotFound(err)) return false;
    throw err;
  }
}
